import { PayloadAction } from '@reduxjs/toolkit';
import { message } from 'antd';
import { QueryClient } from 'react-query';
import { put, takeLatest, select } from 'redux-saga/effects';
import { ProjectId, TaskId } from 'api/models';
import { TaskAPI, TaskSourceType, Task as TaskApi } from 'api/tasks';
import { getQueryClientContext } from 'data/core/sagas/query-client';
import { removeTask } from 'data/home/slice';
import { removeTaskFromSpaceTab } from 'data/space-tabs';
import {
  getProjectDependencyMapKey,
  getProjectTasksQueryKey,
} from 'queries/common';
import {
  DeleteTaskActionPayload,
  deleteTaskSagaAction,
  dependenciesRemovedAction,
} from '../actions';
import { Task } from '../schema';
import { selectTask } from '../selectors';
import { deleteTask } from '../slice';

function* deleteTaskByIdSaga({
  payload,
}: PayloadAction<DeleteTaskActionPayload>): Generator {
  const { projectId, taskId, options } = payload;
  const task = (yield select(selectTask(TaskId(projectId, taskId)))) as Task;
  const queryClient = (yield getQueryClientContext()) as QueryClient;

  if (task) {
    if (task.source && task.source.sourceId) {
      if (task.source.sourceType === TaskSourceType.SpaceTab) {
        yield put(
          removeTaskFromSpaceTab({
            spaceTabId: task.source.sourceId,
            taskId,
          }),
        );
      }
    }

    yield put(removeTask(taskId));

    if (task.dependentTasks && task.dependentTasks.length > 0) {
      const dependencies = task.dependentTasks.map((t) => t.taskId);

      if (dependencies.length > 0) {
        yield put(
          dependenciesRemovedAction({
            taskId,
            dependentTaskIds: dependencies,
          }),
        );
      }
    }
  }

  if (!options?.skipStoreUpdate) {
    yield put(deleteTask(TaskId(projectId, taskId)));
  }

  try {
    yield TaskAPI.deleteTask(projectId, taskId, options?.actor);
    if (!options?.skipRefetch) {
      yield queryClient.refetchQueries(
        getProjectTasksQueryKey(ProjectId(projectId)),
      );
    } else {
      queryClient.setQueryData<TaskApi[]>(
        getProjectTasksQueryKey(ProjectId(Number(projectId))),
        (tasks: TaskApi[] | undefined) => {
          const updatedTasks = tasks?.filter((t) => {
            return t.taskId !== taskId;
          });

          return updatedTasks ?? [];
        },
      );
    }
    yield queryClient.invalidateQueries(getProjectDependencyMapKey(projectId));
  } catch (error) {
    message.error('There was an error processing your request.');
  }
}

export const watchDeleteTaskByIdSaga = function* (): Generator {
  yield takeLatest(deleteTaskSagaAction.type, deleteTaskByIdSaga);
};
